import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useId, useState } from "react";
import { Link } from "react-router-dom";
import { cn, dominionUrl } from "@/lib/utils";
import { api } from "@/services/api";
import type { DeskReviewStatus } from "@/types";

export const DESK_REVIEW_KEY = ["desk-review", "me"] as const;
const MAX_COMMENT = 300;

const CARD = "rounded-2xl border border-[#e3dccb] bg-[#FDF6EC]";
const EYEBROW = "text-xs font-bold uppercase tracking-[2px]";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `d MMM` — spelled out because en-GB's short September is "Sept". */
function signedOff(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
}

function Pill({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className={cn("inline-flex rounded-full px-3 py-1 text-xs font-bold", className)}>
      {children}
    </span>
  );
}

function Star({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-[34px]"
      fill={filled ? "#c2986a" : "none"}
      stroke="#c2986a"
      strokeWidth={1.6}
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z" />
    </svg>
  );
}

function ReportCard({ report }: { report: DeskReviewStatus["report"] }) {
  const date = signedOff(report?.released_at ?? null);
  return (
    <section className={cn(CARD, "flex flex-col gap-3 p-6")}>
      <p className={cn(EYEBROW, "text-[#1E6F4F]")}>Your report</p>
      <h2 className="text-lg font-bold">{report?.topic_title ?? "Your brief"}</h2>
      {report ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Pill className="bg-[#d8f3e4] text-[#0f5236]">Feedback released</Pill>
            {date && <span className="text-[13px] text-[#4f5c52]">Signed off {date}</span>}
          </div>
          <p className="flex items-baseline gap-1.5">
            <span className="text-5xl font-extrabold leading-none">{report.score}</span>
            <span className="text-base text-[#4f5c52]">/ 100</span>
          </p>
          <Link
            to={`/reviews/${report.review_id}`}
            className="font-bold text-[#1E6F4F] underline-offset-4 hover:underline"
          >
            Read your full feedback →
          </Link>
        </>
      ) : (
        <Pill className="self-start bg-[#e7e2d4] text-[#3a4740]">Awaiting sign-off</Pill>
      )}
    </section>
  );
}

function InviteCard() {
  return (
    <section className="flex flex-col gap-3 rounded-2xl bg-[#013220] p-6 text-[#F2EFE3] outline-[3px] outline-[#2AB673] -outline-offset-[3px] outline">
      <p className={cn(EYEBROW, "text-[#7BDBAA]")}>New · Share your review</p>
      <h2 className="text-lg font-bold">How was the Dominion Programme?</h2>
      <p className="text-sm text-[#b6c3b8]">
        Opens once your feedback is signed off. Your review is always kept by us; it only goes
        public if you consent and we approve it.
      </p>
    </section>
  );
}

function StatusCard({
  pill,
  title,
  children,
}: {
  pill: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <section className={cn(CARD, "flex flex-col items-start gap-3 p-7")}>
      {pill}
      <h2 className="text-xl font-extrabold">{title}</h2>
      {children}
    </section>
  );
}

function ReviewForm({ disabled }: { disabled: boolean }) {
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const commentId = useId();
  const consentId = useId();

  const submit = useMutation({
    mutationFn: () => api.submitDeskReview({ rating, comment, consent }),
    onSuccess: (status) => queryClient.setQueryData(DESK_REVIEW_KEY, status),
  });

  const canSubmit = !disabled && consent && rating > 0 && !submit.isPending;

  return (
    <form
      className={cn(CARD, "flex flex-col gap-[18px] p-7")}
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) submit.mutate();
      }}
    >
      <h2 className="text-xl font-extrabold">Your review</h2>

      <fieldset disabled={disabled} className="flex flex-col gap-2">
        <legend className="mb-2 text-sm font-bold">Rating</legend>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} stars`}
              aria-pressed={rating === n}
              onClick={() => setRating(n)}
              className="flex size-11 items-center justify-center rounded-lg focus-visible:outline-2 focus-visible:outline-[#1E6F4F] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Star filled={n <= rating} />
            </button>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-col gap-2">
        <label htmlFor={commentId} className="text-sm font-bold">
          Your review (up to 300 characters)
        </label>
        <textarea
          id={commentId}
          value={comment}
          maxLength={MAX_COMMENT}
          rows={4}
          disabled={disabled}
          onChange={(e) => setComment(e.target.value)}
          className="w-full resize-y rounded-[10px] border border-[#cfc7b3] bg-[#fffdf8] p-3 text-base text-[#013220] disabled:cursor-not-allowed disabled:opacity-60"
        />
        <p className="text-right text-[13px] text-[#4f5c52]" aria-live="polite">
          {comment.length} / {MAX_COMMENT}
        </p>
      </div>

      <div className="flex items-start gap-3">
        <input
          id={consentId}
          type="checkbox"
          checked={consent}
          disabled={disabled}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 size-[22px] shrink-0 accent-[#1E6F4F] disabled:cursor-not-allowed"
        />
        <label htmlFor={consentId} className="text-sm">
          I’m happy for this review, my Report, my name, photo, role, firm and LinkedIn to be
          published on the Dominion website. <strong>Required to submit.</strong>
        </label>
      </div>

      {submit.isError && (
        <p role="alert" className="text-sm font-bold text-[#7a2e29]">
          {submit.error.message || "Couldn’t submit your review. Try again."}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-12 self-start rounded-[10px] bg-[#1E6F4F] px-6 font-bold text-white disabled:cursor-not-allowed disabled:bg-[#b6c3b8] disabled:text-[#3a4740]"
        >
          Submit review
        </button>
        <p className="text-[13px] text-[#4f5c52]">
          Tick the consent box to submit. Reviews can’t be edited after submitting.
        </p>
      </div>
    </form>
  );
}

function ReviewSection({ status }: { status: DeskReviewStatus }) {
  const name = status.first_name ?? "there";
  const review = status.review;

  switch (status.state) {
    case "open":
      return <ReviewForm disabled={false} />;
    case "locked":
      return (
        <>
          <StatusCard
            pill={<Pill className="bg-[#e7e2d4] text-[#3a4740]">Locked</Pill>}
            title="Review opens after your feedback"
          />
          <ReviewForm disabled />
        </>
      );
    case "pending":
      return (
        <StatusCard
          pill={<Pill className="bg-[#fbe6c6] text-[#6b4a14]">Pending approval</Pill>}
          title={`Thanks, ${name} — we’re reviewing it`}
        >
          {review && (
            <>
              <div role="img" aria-label={`${review.rating} out of 5 stars`} className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} filled={n <= review.rating} />
                ))}
              </div>
              {review.comment && <p className="italic">“{review.comment}”</p>}
            </>
          )}
          <p className="text-[13px] text-[#4f5c52]">Read-only — it can’t be edited.</p>
        </StatusCard>
      );
    case "published":
      return (
        <StatusCard
          pill={<Pill className="bg-[#d8f3e4] text-[#0f5236]">Published</Pill>}
          title="Your review is live"
        >
          <a
            href={dominionUrl()}
            className="font-bold text-[#1E6F4F] underline-offset-4 hover:underline"
          >
            See it on the website →
          </a>
        </StatusCard>
      );
    default:
      return (
        <StatusCard
          pill={<Pill className="bg-[#efe1e0] text-[#7a2e29]">Not published</Pill>}
          title="Thanks for your review"
        >
          <p className="text-sm">
            We’ve kept it, but won’t publish it this time. If we need changes, Jonty will get in
            touch.
          </p>
        </StatusCard>
      );
  }
}

/** The dashboard a Desk Participant sees: their report and the review form (KC-508). */
export function DeskDashboard() {
  const { data: status } = useQuery({
    queryKey: DESK_REVIEW_KEY,
    queryFn: () => api.getDeskReview(),
  });

  return (
    <div className="-m-4 min-h-full bg-[#F2EFE3] px-4 py-8 font-[Manrope] text-[#013220] sm:-m-6 md:-m-8 md:px-8">
      {status && (
        <div className="mx-auto flex max-w-4xl flex-col gap-[22px]">
          <h1 className="text-[28px] font-extrabold">
            Welcome back, {status.first_name ?? "there"}
          </h1>
          <div className="grid grid-cols-1 gap-[22px] md:grid-cols-2">
            <ReportCard report={status.report} />
            <InviteCard />
          </div>
          <ReviewSection status={status} />
        </div>
      )}
    </div>
  );
}
